package org.teddapp.project.ui.backup

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddCardTone
import org.teddapp.project.ui.TeddEmptyState
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddScreenHeader
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTab
import org.teddapp.project.ui.TeddTabs
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTagTone
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.teddSurface

/** Marges d'un onglet de l'écran Sauvegarde, les mêmes que celles des autres modules. */
internal val TabPadding = Modifier.padding(start = TeddSpace.S8, end = TeddSpace.S8, top = TeddSpace.S6, bottom = TeddSpace.S12)

private val TileShape = RoundedCornerShape(TeddTokens.RMd)

// ── Vue d'ensemble ─────────────────────────────────────────────────────

@Composable
internal fun OverviewTab(
    history: List<BackupEntry>,
    lastOk: BackupEntry?,
    monthCount: Int,
    hoursSince: Double,
    totalKb: Int,
    modules: List<BackupModule>,
    settings: BackupSettings,
    cloudAvailable: Boolean,
    cloudEnabled: Boolean,
    lastSyncAt: Long?,
    onSettingsChange: (BackupSettings) -> Unit,
    onRunBackup: (BackupFormat) -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).then(TabPadding),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S6),
    ) {
        TeddScreenHeader(
            title = "Sauvegarde",
            subtitle = if (lastOk != null) {
                "Dernière synchronisation réussie le ${fmtDate(lastOk.dayIso)} à ${lastOk.hhmm}."
            } else {
                "Aucune synchronisation pour l'instant. Tes données sont sur cet appareil."
            },
            actions = {
                TeddButton(
                    text = "Synchroniser",
                    onClick = { onRunBackup(BackupFormat.Json) },
                    kind = TeddButtonKind.Primary,
                    enabled = cloudAvailable && cloudEnabled,
                    icon = "repeat",
                )
            },
        )

        Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S4), modifier = Modifier.fillMaxWidth()) {
            StatTile(
                label = "Ce mois",
                value = monthCount.toString(),
                unit = "sur 2 visées",
                signal = if (monthCount >= 2) Signal.Ok else Signal.Warn,
                modifier = Modifier.weight(1f),
            )
            StatTile(
                label = "Dernière",
                value = if (hoursSince.isInfinite()) "—" else hoursSince.toInt().toString(),
                unit = if (hoursSince.isInfinite()) "" else "h plus tôt",
                signal = if (!hoursSince.isInfinite() && hoursSince > 24) Signal.Warn else Signal.None,
                modifier = Modifier.weight(1f),
            )
            StatTile(label = "Volume", value = totalKb.toString(), unit = "Ko", modifier = Modifier.weight(1f))
            StatTile(
                label = "Modules",
                value = modules.count { it.enabled }.toString(),
                unit = "sur ${modules.size}",
                modifier = Modifier.weight(1f),
            )
        }

        PolicyCard(settings = settings, onSettingsChange = onSettingsChange)
        NextBackupCard(
            cloudAvailable = cloudAvailable,
            cloudEnabled = cloudEnabled,
            autoEnabled = settings.autoBackup,
            frequency = settings.frequency,
            lastSyncAt = lastSyncAt,
        )

        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            SectionLabel("Activité récente")
            if (history.isEmpty()) {
                Text("Aucun cycle pour l'instant.", color = TeddTokens.InkMuted, style = TeddType.Body)
            } else {
                Column(modifier = Modifier.fillMaxWidth().teddSurface(TileShape)) {
                    history.take(6).forEachIndexed { i, entry -> HistoryRow(entry = entry, first = i == 0) }
                }
            }
        }
    }
}

private enum class Signal { None, Ok, Warn }

/** Un chiffre du tableau de bord. Le chiffre reste en encre ; un signal, s'il y en a un, passe par une icône. */
@Composable
private fun StatTile(
    label: String,
    value: String,
    unit: String,
    modifier: Modifier = Modifier,
    signal: Signal = Signal.None,
) {
    TeddCard(modifier = modifier, shape = TileShape, padding = PaddingValues(TeddSpace.S4)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            Text(label.uppercase(), color = TeddTokens.InkMuted, style = TeddType.Overline, modifier = Modifier.weight(1f), maxLines = 1)
            when (signal) {
                Signal.Ok -> TeddIcon(name = "check", size = 14.dp, color = TeddTokens.Success, strokeWidth = 2.dp)
                Signal.Warn -> TeddIcon(name = "alert", size = 14.dp, color = TeddTokens.Warning)
                Signal.None -> Unit
            }
        }
        Spacer(Modifier.height(TeddSpace.S2))
        Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            Text(value, color = TeddTokens.Ink, style = TeddType.Title1)
            if (unit.isNotEmpty()) {
                Text(unit, color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.padding(bottom = 5.dp), maxLines = 1)
            }
        }
    }
}

@Composable
private fun PolicyCard(
    settings: BackupSettings,
    onSettingsChange: (BackupSettings) -> Unit,
    modifier: Modifier = Modifier,
) {
    TeddCard(modifier = modifier.fillMaxWidth(), shape = TileShape, padding = PaddingValues(TeddSpace.S5)) {
        SectionLabel("Planification")
        Spacer(Modifier.height(TeddSpace.S4))
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
            PolicyRow(label = "Synchronisation automatique") {
                ToggleSwitch(on = settings.autoBackup, onChange = { onSettingsChange(settings.copy(autoBackup = !settings.autoBackup)) })
            }
            PolicyRow(label = "Fréquence") {
                TeddTabs(
                    tabs = BackupFrequency.entries.map { TeddTab(it.key, it.label) },
                    selected = settings.frequency.key,
                    onSelect = { onSettingsChange(settings.copy(frequency = BackupFrequency.fromKey(it))) },
                )
            }
        }
        Spacer(Modifier.height(TeddSpace.S4))
        Text(
            text = "La planification ne tourne que si la synchronisation est active. " +
                "La base locale, elle, est toujours là : l'onglet Exporter en sort une copie quand tu veux.",
            color = TeddTokens.InkMuted,
            style = TeddType.Caption,
        )
    }
}

@Composable
private fun PolicyRow(label: String, right: @Composable () -> Unit) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Text(label, color = TeddTokens.Ink, style = TeddType.Label, modifier = Modifier.weight(1f))
        right()
    }
}

@Composable
private fun NextBackupCard(
    cloudAvailable: Boolean,
    cloudEnabled: Boolean,
    autoEnabled: Boolean,
    frequency: BackupFrequency,
    lastSyncAt: Long?,
    modifier: Modifier = Modifier,
) {
    val label = nextScheduledLabel(
        cloudAvailable = cloudAvailable,
        cloudEnabled = cloudEnabled,
        autoEnabled = autoEnabled,
        frequency = frequency,
        lastSyncAt = lastSyncAt,
        now = org.teddapp.project.db.nowMillis(),
    )
    val subtitle = when {
        !cloudAvailable -> "La planification a besoin d'un projet Supabase relié."
        !cloudEnabled -> "Active la synchronisation, dans la colonne de gauche, pour la relancer."
        !autoEnabled -> "Synchronisation automatique coupée : à toi de la lancer."
        else -> "Fréquence : ${frequency.label.lowercase()}."
    }
    TeddCard(modifier = modifier.fillMaxWidth(), shape = TileShape, padding = PaddingValues(TeddSpace.S5), tone = TeddCardTone.Sunken) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                SectionLabel("Prochaine synchronisation")
                Text(label, color = TeddTokens.Ink, style = TeddType.Title2)
            }
            Text(subtitle, color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.weight(1f))
        }
    }
}

@Composable
private fun SectionLabel(text: String) {
    Text(text.uppercase(), color = TeddTokens.InkMuted, style = TeddType.Overline)
}

// ── Historique ─────────────────────────────────────────────────────────

@Composable
internal fun HistoryTab(history: List<BackupEntry>) {
    val groups: Map<String, List<BackupEntry>> = remember(history) {
        // toSortedMap n'existe pas en KMP : on construit un LinkedHashMap trié à la main.
        val grouped = history.groupBy { it.dayIso }
        val result = linkedMapOf<String, List<BackupEntry>>()
        for (k in grouped.keys.sortedDescending()) result[k] = grouped.getValue(k)
        result
    }
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(start = TeddSpace.S8, end = TeddSpace.S8, top = TeddSpace.S6, bottom = TeddSpace.S12),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S5),
    ) {
        item {
            TeddScreenHeader(
                title = "Historique",
                subtitle = if (history.isEmpty()) "Chaque synchronisation laisse une ligne ici."
                else "${history.size} cycle${if (history.size > 1) "s" else ""} sur ${groups.size} jour${if (groups.size > 1) "s" else ""}.",
            )
        }
        if (history.isEmpty()) {
            item {
                TeddEmptyState(
                    title = "Aucun cycle enregistré",
                    message = "L'historique se remplit à la première synchronisation.",
                    icon = "clock",
                )
            }
        }
        groups.forEach { (day, entries) ->
            item(key = day) {
                Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
                        modifier = Modifier.fillMaxWidth().padding(horizontal = TeddSpace.S1),
                    ) {
                        Text(fmtDayLabel(day).uppercase(), color = TeddTokens.InkMuted, style = TeddType.Overline)
                        Text(fmtDate(day), color = TeddTokens.InkSubtle, style = TeddType.MonoSm.copy(fontSize = 11.sp))
                        Spacer(modifier = Modifier.weight(1f))
                        Text(
                            text = "${entries.size} cycle${if (entries.size > 1) "s" else ""}",
                            color = TeddTokens.InkMuted,
                            style = TeddType.MonoSm.copy(fontSize = 11.sp),
                        )
                    }
                    Column(modifier = Modifier.fillMaxWidth().teddSurface(TileShape)) {
                        entries.forEachIndexed { i, e -> HistoryRow(entry = e, first = i == 0) }
                    }
                }
            }
        }
    }
}

@Composable
internal fun HistoryRow(entry: BackupEntry, first: Boolean) {
    val (statusColor, statusIcon, statusWord) = when (entry.status) {
        BackupStatus.Success -> Triple(TeddTokens.Success, "check", "Réussie")
        BackupStatus.Partial -> Triple(TeddTokens.Warning, "alert", "Partielle")
        BackupStatus.Failed -> Triple(TeddTokens.Danger, "alert", "Échouée")
    }
    Column {
        if (!first) Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = TeddSpace.S4, vertical = TeddSpace.S3),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
        ) {
            TeddIcon(name = statusIcon, size = 18.dp, color = statusColor, strokeWidth = 2.dp)
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    Text("$statusWord · ${entry.trigger.label}", color = TeddTokens.Ink, style = TeddType.Label)
                    TeddTag(text = entry.destination.label, tone = TeddTagTone.Outline)
                }
                entry.note?.let { Text(it, color = TeddTokens.InkMuted, style = TeddType.Caption) }
            }
            Column(horizontalAlignment = Alignment.End) {
                Text(entry.hhmm, color = TeddTokens.Ink, style = TeddType.MonoSm)
                Text(
                    text = "${entry.sizeKb} ligne${if (entry.sizeKb > 1) "s" else ""} · ${formatDuration(entry.durationSec)} s",
                    color = TeddTokens.InkMuted,
                    style = TeddType.MonoSm.copy(fontSize = 11.sp),
                )
            }
        }
    }
}

private fun formatDuration(d: Double): String {
    val tenths = (d * 10).toInt()
    return "${tenths / 10},${tenths % 10}"
}

// ── Exporter ───────────────────────────────────────────────────────────

@Composable
internal fun ExportTab(
    modules: List<BackupModule>,
    totalKb: Int,
    cloudAvailable: Boolean,
    onSyncCloud: () -> Unit,
    onDownload: ((BackupFormat) -> Unit)? = null,
) {
    Column(
        modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).then(TabPadding),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S6),
    ) {
        TeddScreenHeader(
            title = "Exporter",
            subtitle = "Un fichier de toute la base, à garder, à envoyer ou à archiver toi-même.",
        )
        Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S4), modifier = Modifier.fillMaxWidth()) {
            ExportCard(
                format = "JSON",
                desc = "Lisible et portable. Se réimporte dans teddApp, ou se lit depuis un script.",
                icon = "notes",
                size = totalKb,
                onDownload = { onDownload?.invoke(BackupFormat.Json) },
                onSync = onSyncCloud,
                downloadEnabled = onDownload != null,
                syncEnabled = cloudAvailable,
                modifier = Modifier.weight(1f),
            )
            ExportCard(
                format = "SQL",
                desc = "Des instructions INSERT pour PostgreSQL, compatibles Supabase. Le schéma est à part : supabase_schema.sql.",
                icon = "list",
                size = (totalKb * 1.35).toInt(),
                onDownload = { onDownload?.invoke(BackupFormat.Sql) },
                onSync = null,
                downloadEnabled = onDownload != null,
                syncEnabled = false,
                modifier = Modifier.weight(1f),
            )
        }
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            SectionLabel("Modules inclus")
            ChipFlow(modules = modules.filter { it.enabled })
        }
    }
}

@Composable
private fun ExportCard(
    format: String,
    desc: String,
    icon: String,
    size: Int,
    onDownload: () -> Unit,
    onSync: (() -> Unit)?,
    downloadEnabled: Boolean,
    syncEnabled: Boolean,
    modifier: Modifier = Modifier,
) {
    TeddCard(
        modifier = modifier,
        title = format,
        icon = icon,
        action = { Text("≈ $size Ko", color = TeddTokens.InkMuted, style = TeddType.MonoSm) },
    ) {
        Text(desc, color = TeddTokens.InkMuted, style = TeddType.Body, modifier = Modifier.height(66.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2), modifier = Modifier.padding(top = TeddSpace.S4)) {
            TeddButton(
                text = "Télécharger",
                onClick = onDownload,
                kind = TeddButtonKind.Primary,
                enabled = downloadEnabled,
                icon = "arrow-down",
            )
            if (onSync != null) {
                TeddButton(
                    text = "Synchroniser",
                    onClick = onSync,
                    kind = TeddButtonKind.Secondary,
                    enabled = syncEnabled,
                    icon = "repeat",
                )
            }
        }
        if (!downloadEnabled) {
            Text(
                "Le téléchargement n'est pas disponible sur cette plateforme.",
                color = TeddTokens.InkMuted,
                style = TeddType.Caption,
                modifier = Modifier.padding(top = TeddSpace.S2),
            )
        }
    }
}

@Composable
private fun ChipFlow(modules: List<BackupModule>) {
    FlowRow(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2), verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        modules.forEach { ModuleChip(module = it) }
    }
}

@Composable
private fun ModuleChip(module: BackupModule) {
    TeddTag(text = "${module.name} · ${module.sizeKb} Ko", icon = module.icon)
}

// ── Modules ────────────────────────────────────────────────────────────

@Composable
internal fun ModulesTab(modules: List<BackupModule>) {
    val totalRows = modules.sumOf { it.rowCount }
    val totalKb = modules.sumOf { it.sizeKb }
    Column(
        modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).then(TabPadding),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S6),
    ) {
        TeddScreenHeader(
            title = "Modules inclus",
            subtitle = "Ce que contient la base, module par module : $totalRows ligne${if (totalRows > 1) "s" else ""}, environ $totalKb Ko. " +
                "Tout part et revient ensemble à chaque synchronisation.",
        )
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
            modules.chunked(3).forEach { row ->
                Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S4), modifier = Modifier.fillMaxWidth()) {
                    row.forEach { m -> ModuleCard(module = m, modifier = Modifier.weight(1f)) }
                    repeat(3 - row.size) { Spacer(modifier = Modifier.weight(1f)) }
                }
            }
        }
    }
}

@Composable
private fun ModuleCard(module: BackupModule, modifier: Modifier = Modifier) {
    val hasRows = module.rowCount > 0
    TeddCard(modifier = modifier, shape = TileShape, padding = PaddingValues(TeddSpace.S4)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            Box(
                modifier = Modifier.size(36.dp).background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm)),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = module.icon, size = 18.dp, color = if (hasRows) module.color else TeddTokens.InkSubtle)
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(module.name, color = if (hasRows) TeddTokens.Ink else TeddTokens.InkMuted, style = TeddType.BodyStrong, maxLines = 1, overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis)
                Text(
                    text = if (hasRows) "${module.rowCount} ligne${if (module.rowCount > 1) "s" else ""} · ${module.sizeKb} Ko" else "vide",
                    color = TeddTokens.InkMuted,
                    style = TeddType.MonoSm.copy(fontSize = 11.sp),
                )
            }
        }
    }
}
